import { formatMonth, type PDate } from "@/plan-logic/lib/dates";
import { money } from "@/plan-logic/lib/format";
import type { GroupRrspMatch } from "@/plan-logic/plan-view";

const pct = (r: number) => `${+(r * 100).toFixed(2)}%`;

const WHY =
  "the year's take-home pay and spare cash first have to cover the bills (or RRSP room runs short). Abi checks again every year.";

/** How many of the plan's later changes the sentence names; the bars show the rest. */
const NAMED_CHANGES = 2;

/**
 * What Abi has the employee contribute, over the years the bars show (QA 7: "the full 5% of pay"
 * over bars that fall to nothing two years later): the rate now, then each later change the plan
 * makes, so the sentence is true for the whole period and not only its first year.
 */
export function rateLine(m: GroupRrspMatch): string {
  const later = m.changes;
  const now = later.length ? " for now" : "";
  let line: string;
  if (m.currentRate <= 0)
    line = `Right now Abi has ${m.firstName} contributing nothing, because ${WHY}`;
  else if (m.currentRate < m.capRate)
    line = `Abi has ${m.firstName} contributing ${pct(m.currentRate)} of pay${now}, below the ${pct(m.capRate)} the employer would match, because ${WHY}`;
  else
    line = later.length
      ? `Abi has ${m.firstName} contributing the full ${pct(m.capRate)} of pay for now.`
      : `Abi has ${m.firstName} contributing the full ${pct(m.capRate)} of pay, so none of the match is left unclaimed.`;
  let before = m.currentRate;
  let explained = m.currentRate < m.capRate;
  const said = later.slice(0, NAMED_CHANGES).map((c) => {
    const to = c.rate >= m.capRate ? `the full ${pct(m.capRate)}` : pct(c.rate);
    const what =
      c.rate <= 0
        ? `has ${m.firstName} stop contributing`
        : before <= 0
          ? `has ${m.firstName} contribute ${to}`
          : `${c.rate < before ? "lowers" : "raises"} it to ${to}`;
    const why = c.rate < before && !explained ? `, because ${WHY.replace(/\.[^.]*\.$/, "")}` : "";
    if (why) explained = true;
    before = c.rate;
    return `from ${c.year} Abi ${what}${why}`;
  });
  if (!said.length) return line;
  const rest = later.length - said.length;
  const tail =
    rest > 0
      ? ` After that it changes ${rest} more time${rest === 1 ? "" : "s"}; the bars show each year.`
      : "";
  const joined = said.join(", and ");
  return `${line} ${joined[0].toUpperCase()}${joined.slice(1)}.${tail}`;
}

/**
 * `stopsAt`: the plan's first shortfall, where the engine stops it, so the bars (and the totals)
 * end there.
 */
export function GroupRrspCard({
  matches,
  stopsAt,
}: {
  matches: GroupRrspMatch[];
  stopsAt?: PDate;
}) {
  return (
    <div className="card pad mt" data-testid="home-group-rrsp">
      <h2>Your employer RRSP match</h2>
      {matches.map((m) => {
        const top = Math.max(1, ...m.years.map((y) => y.you + y.employer));
        // The plan stops at its shortfall: when that comes before the contributions would end, the
        // bars and totals end there rather than at retirement.
        const cut = stopsAt && m.years.length && stopsAt.year <= m.years.at(-1)!.year;
        return (
          <div key={m.jobUuid} className="mt" data-testid="group-rrsp-match">
            <b>
              {m.firstName}&rsquo;s group RRSP{m.employer ? ` at ${m.employer}` : ""}
            </b>
            <p>
              {m.employer || "The employer"} matches {pct(m.matchingRate)} of what {m.firstName}{" "}
              puts in, up to {pct(m.capRate)} of pay. Contributions come off pay before tax, and the
              match is free money for retirement.
            </p>
            <p>{rateLine(m)}</p>
            {m.years.length > 0 && (
              <>
                <p>
                  {cut
                    ? `Until the plan runs short in ${formatMonth(stopsAt)}`
                    : "Until retirement"}
                  , {m.firstName} puts in <b className="tnum">{money(m.totalYou)}</b> and the
                  employer adds <b className="tnum">{money(m.totalEmployer)}</b>.
                </p>
                <div
                  style={{ display: "flex", alignItems: "flex-end", gap: 2, height: 120 }}
                  aria-label="Group RRSP contributions by year"
                  data-testid="group-rrsp-bars"
                >
                  {m.years.map((y) => (
                    <div
                      key={y.year}
                      title={`${y.year}: ${money(y.you)} from pay, ${money(y.employer)} from the employer`}
                      style={{
                        flex: 1,
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "flex-end",
                      }}
                    >
                      <span
                        style={{
                          height: `${(y.employer / top) * 100}%`,
                          background: "var(--save)",
                        }}
                      />
                      <span
                        style={{ height: `${(y.you / top) * 100}%`, background: "var(--ess)" }}
                      />
                    </div>
                  ))}
                </div>
                <p className="mut">
                  {m.years.length === 1 ? (
                    m.years[0].year
                  ) : (
                    <>
                      {m.years[0].year}&ndash;{m.years.at(-1)!.year}
                    </>
                  )}
                  : from pay (lower) and from the employer (upper), one bar a year.
                  {cut && ` The plan stops in ${formatMonth(stopsAt)}, when it runs short.`}
                </p>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}
